import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ShieldCheck, Lock } from "lucide-react";
import ReportContent from "../components/ReportContent";
import Highlights from "../components/Highlights";
import { API } from "../lib/api";

const publicApi = axios.create({ baseURL: API, withCredentials: false });

export default function DonorProgramReport() {
  const { token, pid } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    publicApi
      .get(`/public/donor/${token}/program/${pid}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "This program is not available."));
  }, [token, pid]);

  const auditFetcher = async (indicator_id, period) => {
    const r = await publicApi.get(`/public/donor/${token}/program/${pid}/audit`, {
      params: { indicator_id, period },
    });
    return r.data;
  };

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div className="max-w-md text-center bg-white border border-slate-200 p-10 rounded-sm" data-testid="donor-program-error">
          <Lock className="w-10 h-10 mx-auto text-slate-400" />
          <h1 className="font-heading text-2xl mt-4">Program unavailable</h1>
          <p className="text-slate-600 mt-2 text-sm">{error}</p>
          <Link to={`/donor/${token}`} className="text-emerald-700 text-sm underline mt-4 inline-block">
            ← Back to portal
          </Link>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;

  const branding = data.branding || {};
  const orgLogo = branding.org_logo_url;
  const orgName = branding.org_name || "Array Engine";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to={`/donor/${token}`} className="flex items-center gap-3 group" data-testid="donor-back-link">
            <ArrowLeft className="w-4 h-4 text-slate-400 group-hover:text-emerald-600" />
            {orgLogo ? (
              <img src={orgLogo} alt={orgName} className="h-7 w-auto max-w-[180px] object-contain" />
            ) : (
              <img src="/array-logo.png" alt="Array Engine" className="h-7 w-auto" />
            )}
          </Link>
          <span className="hidden sm:flex items-center gap-1 text-xs text-slate-500">
            <ShieldCheck className="w-3 h-3" /> {data.donor?.donor_name} · view-only
          </span>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-2 sm:px-6 py-10" data-testid="donor-program-report">
        {data.highlights?.length > 0 && (
          <div className="mb-6 px-2 sm:px-0">
            <p className="label-overline mb-3">Impact at a glance</p>
            <Highlights items={data.highlights} />
          </div>
        )}
        <div className="bg-white border border-slate-200 rounded-sm">
          <ReportContent data={data} report={data.report} auditFetcher={auditFetcher} />
          <div className="px-6 py-4 border-t border-slate-100 text-xs text-slate-500 flex items-center justify-between flex-wrap gap-2">
            <span>Numbers shown are aggregated from validated source data. Click any value to inspect the audit trail.</span>
            <span className="label-overline">
              {orgLogo ? `${orgName} · ` : ""}Powered by Array Engine
            </span>
          </div>
        </div>
      </main>
    </div>
  );
}
